import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { fields, generateTimeSlots, getDisplayPrice, getPriceForHour, preBookedSlots } from '../../data/mockData';
import { getOrCreateSubscriberId, isSlotBlockedBySubscription } from '../../data/subscriptionStore';
import { createBooking } from '../../data/bookingStore';
import { getCustomerById, getSession } from '../../data/authStore';
import { joinWaitlist, processDueReminders } from '../../data/engagementStore';

type Step = 'select' | 'confirm' | 'success';

const BookingPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const field = fields.find(f => f.id === id);
  const session = getSession();
  const customer = session?.role === 'customer' ? getCustomerById(session.userId) : undefined;
  const customerSessionUserId = session?.role === 'customer' ? session.userId : '';
  const customerName = customer?.fullName || '';
  const customerPhone = customer?.phone || '';

  const [step, setStep] = useState<Step>('select');
  const [error, setError] = useState('');
  const [reservationCode, setReservationCode] = useState('');

  // Step 1 - Date/Time Selection
  const [selectedDate, setSelectedDate] = useState(searchParams.get('date') || (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })());
  const [selectedHour, setSelectedHour] = useState<number | null>(
    searchParams.get('hour') ? Number(searchParams.get('hour')) : null
  );
  const [duration] = useState(1);

  // Step 2 - Contact Info
  const [teamName, setTeamName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [waitlistHour, setWaitlistHour] = useState<number | null>(null);
  const [waitlistName, setWaitlistName] = useState('');
  const [waitlistPhone, setWaitlistPhone] = useState('');
  const [waitlistError, setWaitlistError] = useState('');
  const [waitlistSuccess, setWaitlistSuccess] = useState('');
  const [dispatchInfo, setDispatchInfo] = useState('');
  const subscriberId = useMemo(
    () => (session?.role === 'customer' ? `cust-${customerSessionUserId}` : getOrCreateSubscriberId()),
    [session?.role, customerSessionUserId]
  );

  useEffect(() => {
    if (!customerSessionUserId) return;
    if (!customerName || !customerPhone) return;
    setContactName((prev) => prev || customerName);
    setContactPhone((prev) => prev || customerPhone);
    setWaitlistName((prev) => prev || customerName);
    setWaitlistPhone((prev) => prev || customerPhone);
  }, [customerSessionUserId, customerName, customerPhone]);

  useEffect(() => {
    processDueReminders();
  }, []);

  const timeSlots = useMemo(() => {
    if (!field) return [];
    return generateTimeSlots(field.id, selectedDate);
  }, [field, selectedDate]);

  const weekDates = useMemo(() => {
    const dates: { date: string; label: string; dayName: string }[] = [];
    const dayNames = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];
    for (let i = 0; i < 14; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      dates.push({
        date: dateStr,
        label: `${d.getDate()} ${['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'][d.getMonth()]}`,
        dayName: dayNames[d.getDay()],
      });
    }
    return dates;
  }, []);

  const isSlotAvailable = (hour: number, label: string) => {
    if (field?.isReservationPaused) return false;
    const preBooked = preBookedSlots.some(
      (s) => s.fieldId === id && s.date === selectedDate && s.hour === hour
    );
    const blockedBySubscription = id
      ? isSlotBlockedBySubscription(id, selectedDate, label, subscriberId)
      : false;
    return !preBooked && !blockedBySubscription;
  };

  const slotDurationHours = 1;
  const selectedHourPrice = field && selectedHour !== null ? getPriceForHour(field, selectedHour) : field?.pricePerHour || 0;
  const displayPrice = field ? getDisplayPrice(field) : 0;
  const baseTotalPrice = field ? selectedHourPrice * slotDurationHours * duration : 0;
  const totalPrice = baseTotalPrice;
  const unavailableSlots = useMemo(
    () => timeSlots.filter((slot) => !isSlotAvailable(slot.hour, slot.label || formatHourValue(slot.hour))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [timeSlots, selectedDate, field?.id, subscriberId]
  );

  useEffect(() => {
    if (unavailableSlots.length === 0) {
      setWaitlistHour(null);
      return;
    }
    setWaitlistHour((prev) => {
      if (prev !== null && unavailableSlots.some((slot) => Math.abs(slot.hour - prev) < 0.001)) {
        return prev;
      }
      return unavailableSlots[0].hour;
    });
  }, [unavailableSlots]);

  const handleJoinWaitlist = () => {
    if (!field) return;
    if (waitlistHour === null) {
      setWaitlistError('Lütfen bekleme listesi için dolu bir saat seçin.');
      setWaitlistSuccess('');
      return;
    }
    const slot = unavailableSlots.find((item) => Math.abs(item.hour - waitlistHour) < 0.001);
    if (!slot) {
      setWaitlistError('Seçilen saat artık dolu görünmüyor. Lütfen listeyi güncelleyin.');
      setWaitlistSuccess('');
      return;
    }

    const result = joinWaitlist({
      fieldId: field.id,
      fieldName: field.name,
      date: selectedDate,
      hour: waitlistHour,
      label: slot.label || `${formatHourValue(waitlistHour)}-${formatHourValue(waitlistHour + 1)}`,
      customerId: session?.role === 'customer' ? session.userId : undefined,
      contactName: waitlistName,
      contactPhone: waitlistPhone,
    });

    if (!result.ok) {
      setWaitlistError(result.error);
      setWaitlistSuccess('');
      return;
    }

    setWaitlistError('');
    setWaitlistSuccess('Bekleme listesine eklendiniz. Saat açılırsa profilinizde bildirim göreceksiniz.');
  };
  function formatHourValue(value: number) {
    const totalMinutes = Math.round(value * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  function normalizePhoneInput(value: string) {
    return value.replace(/[^\d+\s()-]/g, '');
  }

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
    const days = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}, ${days[d.getDay()]}`;
  };

  if (!field) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-gray-900 text-2xl font-bold mb-4">Saha Bulunamadı</h2>
          <button onClick={() => navigate('/sahalar')} className="text-primary-600 hover:text-primary-700">
            Sahalara Dön
          </button>
        </div>
      </div>
    );
  }

  if (field.isReservationPaused) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl border border-rose-200 p-6 text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Rezervasyon Alımı Durduruldu</h2>
          <p className="text-sm text-gray-600 mb-5">
            {field.reservationPausedMessage || 'Şuanlık rezervasyon alımı durmuştur.'}
          </p>
          <button
            type="button"
            onClick={() => navigate(`/saha/${field.id}`)}
            className="rounded-lg bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 text-sm"
          >
            Saha Profiline Dön
          </button>
        </div>
      </div>
    );
  }

  const handlePaymentConfirm = () => {
    if (selectedHour === null) return;
    setError('');
    setDispatchInfo('');
    const result = createBooking({
      fieldId: field.id,
      fieldName: field.name,
      date: selectedDate,
      hour: selectedHour,
      duration: slotDurationHours * duration,
      teamName,
      contactName,
      contactPhone,
      totalPrice,
      customerId: session?.role === 'customer' ? session.userId : undefined,
    });
    if (!result.ok) {
      setError(result.error);
      setStep('select');
      return;
    }
    setReservationCode(result.booking.id.toUpperCase());
    setDispatchInfo('Talebiniz saha sahibine iletildi. SS onayından sonra ödeme adımı profilinizde açılacak.');

    setStep('success');
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-10">
          {(['select', 'confirm', 'success'] as Step[]).map((s, idx) => {
            const stepLabels = ['Tarih & Saat', 'Bilgiler & Onay', 'Tamamlandı'];
            const currentIdx = ['select', 'confirm', 'success'].indexOf(step);
            const isCompleted = idx < currentIdx;
            const isCurrent = idx === currentIdx;

            return (
              <React.Fragment key={s}>
                {idx > 0 && (
                  <div className={`w-12 md:w-24 h-0.5 ${isCompleted ? 'bg-primary-500' : 'bg-gray-200'}`} />
                )}
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                      isCompleted
                        ? 'bg-primary-500 text-white'
                        : isCurrent
                        ? 'bg-primary-600 text-white ring-4 ring-primary-100'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {isCompleted ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      idx + 1
                    )}
                  </div>
                  <span className={`text-xs mt-2 hidden md:block ${isCurrent ? 'text-primary-600 font-medium' : 'text-gray-500'}`}>
                    {stepLabels[idx]}
                  </span>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Field Info Banner */}
        {step !== 'success' && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-6 flex items-center space-x-4">
            <img src={field.images[0]} alt={field.name} className="w-16 h-16 rounded-lg object-cover" />
            <div>
              <h2 className="text-gray-900 font-semibold">{field.name}</h2>
              <p className="text-gray-500 text-sm">{field.district} &bull; {(field.sizeOptions || [field.size]).join(', ')} &bull; {displayPrice}₺/saat</p>
            </div>
          </div>
        )}

        {/* Step 1: Date & Time Selection */}
        {step === 'select' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h3 className="text-gray-900 font-semibold text-lg mb-4">Tarih Seçin</h3>
              <div className="flex space-x-2 overflow-x-auto pb-2">
                {weekDates.map(({ date, label, dayName }) => (
                  <button
                    key={date}
                    onClick={() => { setSelectedDate(date); setSelectedHour(null); }}
                    className={`flex-shrink-0 px-4 py-3 rounded-xl text-center transition-colors min-w-[80px] ${
                      selectedDate === date
                        ? 'bg-primary-600 text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <div className="text-xs font-medium">{dayName}</div>
                    <div className="text-sm font-semibold mt-1">{label}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <p className="text-sm text-gray-500 mb-4">Seans süresi: 60 dakika</p>

              <h3 className="text-gray-900 font-semibold text-lg mb-4">Saat Seçin</h3>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                {timeSlots.map((slot) => {
                  const label = slot.label || formatHourValue(slot.hour);
                  const available = isSlotAvailable(slot.hour, label);
                  const isSelected = selectedHour === slot.hour;
                  return (
                    <button
                      key={slot.hour}
                      disabled={!available}
                      onClick={() => setSelectedHour(slot.hour)}
                      className={`py-3 px-2 rounded-lg text-sm font-medium transition-all ${
                        !available
                          ? 'bg-red-50 border border-red-300 text-red-400 cursor-not-allowed'
                          : isSelected
                          ? 'bg-primary-600 text-white ring-2 ring-primary-300'
                          : 'bg-green-50 border border-green-300 text-green-700 hover:bg-green-100'
                      }`}
                    >
                      <div>{label}</div>
                      <div className="text-xs mt-1 opacity-75">
                        {!available ? 'Dolu' : 'Müsait'}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {unavailableSlots.length > 0 && (
              <div className="bg-white rounded-xl border border-amber-200 shadow-sm p-6">
                <h3 className="text-gray-900 font-semibold text-lg mb-2">Bekleme Listesi</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Dolu saatler için sıraya girebilirsiniz. İptal olursa otomatik haber verilir.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <select
                    value={waitlistHour ?? ''}
                    onChange={(e) => setWaitlistHour(Number(e.target.value))}
                    className="bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-sm"
                  >
                    {unavailableSlots.map((slot) => (
                      <option key={slot.hour} value={slot.hour}>
                        {slot.label || `${formatHourValue(slot.hour)}-${formatHourValue(slot.hour + 1)}`}
                      </option>
                    ))}
                  </select>
                  <input
                    value={waitlistName}
                    onChange={(e) => setWaitlistName(e.target.value)}
                    placeholder="Ad Soyad"
                    className="bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-sm"
                  />
                  <input
                    value={waitlistPhone}
                    onChange={(e) => setWaitlistPhone(normalizePhoneInput(e.target.value))}
                    placeholder="05XX XXX XX XX"
                    name="waitlist_phone"
                    autoComplete="tel"
                    inputMode="tel"
                    spellCheck={false}
                    className="bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-sm"
                  />
                </div>

                <button
                  type="button"
                  onClick={handleJoinWaitlist}
                  className="rounded-lg bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 text-sm font-medium"
                >
                  Bekleme Listesine Katıl
                </button>

                {waitlistError && <p className="text-xs text-rose-600 mt-2">{waitlistError}</p>}
                {waitlistSuccess && <p className="text-xs text-emerald-700 mt-2">{waitlistSuccess}</p>}
              </div>
            )}

            {selectedHour !== null && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center justify-between">
                <div>
                  <p className="text-gray-900 font-medium">
                    {formatDate(selectedDate)}
                  </p>
                  <p className="text-gray-500 text-sm">
                    {formatHourValue(selectedHour)} - {formatHourValue(selectedHour + slotDurationHours)} (60 dk)
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-primary-600 font-bold text-xl">{totalPrice}₺</p>
                  <p className="text-gray-500 text-xs">Toplam</p>
                </div>
              </div>
            )}

            <button
              disabled={selectedHour === null}
              onClick={() => {
                setError('');
                setStep('confirm');
              }}
              className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-gray-200 disabled:text-gray-400 text-white font-semibold py-3 rounded-lg transition-colors"
            >
              Devam Et
            </button>
          </div>
        )}

        {/* Step 2: Confirmation */}
        {step === 'confirm' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h3 className="text-gray-900 font-semibold text-lg mb-6">Takım & İletişim Bilgileri</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Takım Adı *</label>
                  <input
                    type="text"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    placeholder="ör. Yıldız Spor"
                    className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">İletişim Kişisi *</label>
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="Adınız Soyadınız"
                    className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Telefon Numarası *</label>
                  <input
                    type="text"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(normalizePhoneInput(e.target.value))}
                    placeholder="05XX XXX XX XX"
                    name="contact_phone"
                    autoComplete="tel"
                    inputMode="tel"
                    spellCheck={false}
                    className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Notlar (Opsiyonel)</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder="Ek bilgi veya istekleriniz..."
                    className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors resize-none"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h3 className="text-gray-900 font-semibold text-lg mb-6">Rezervasyon Özeti</h3>

              <div className="space-y-4">
                <div className="flex justify-between py-3 border-b border-gray-200">
                  <span className="text-gray-500">Saha</span>
                  <span className="text-gray-900 font-medium">{field.name}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-gray-200">
                  <span className="text-gray-500">Tarih</span>
                  <span className="text-gray-900 font-medium">{formatDate(selectedDate)}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-gray-200">
                  <span className="text-gray-500">Saat</span>
                  <span className="text-gray-900 font-medium">
                    {formatHourValue(selectedHour || 0)} - {formatHourValue((selectedHour || 0) + slotDurationHours)}
                  </span>
                </div>
                <div className="flex justify-between py-3 border-b border-gray-200">
                  <span className="text-gray-500">Süre</span>
                  <span className="text-gray-900 font-medium">60 Dakika</span>
                </div>
                <div className="flex justify-between py-3 border-b border-gray-200">
                  <span className="text-gray-500">Takım</span>
                  <span className="text-gray-900 font-medium">{teamName}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-gray-200">
                  <span className="text-gray-500">İletişim</span>
                  <span className="text-gray-900 font-medium">{contactName}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-gray-200">
                  <span className="text-gray-500">Telefon</span>
                  <span className="text-gray-900 font-medium">{contactPhone}</span>
                </div>
                {notes && (
                  <div className="flex justify-between py-3 border-b border-gray-200">
                    <span className="text-gray-500">Not</span>
                    <span className="text-gray-900 font-medium max-w-xs text-right">{notes}</span>
                  </div>
                )}
              </div>

              <div className="mt-6 bg-gray-50 rounded-lg p-4 flex items-center justify-between">
                <span className="text-gray-900 font-semibold text-lg">Toplam Tutar</span>
                <div className="text-right">
                  <span className="text-primary-600 font-bold text-2xl">{totalPrice}₺</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h3 className="text-gray-900 font-semibold text-lg mb-4">Ödeme Bilgileri</h3>
              {field.paymentIban && field.paymentBankName && field.paymentAccountName ? (
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-indigo-700 text-xs">Banka</p>
                      <p className="text-indigo-900 font-medium">{field.paymentBankName}</p>
                    </div>
                    <div>
                      <p className="text-indigo-700 text-xs">Hesap Sahibi</p>
                      <p className="text-indigo-900 font-medium">{field.paymentAccountName}</p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-indigo-700 text-xs">IBAN</p>
                      <p className="text-indigo-900 font-medium break-all">{field.paymentIban}</p>
                    </div>
                  </div>
                  {field.paymentNote && (
                    <div className="border-t border-indigo-200 pt-3">
                      <p className="text-indigo-700 text-xs">Saha Sahibinin Notu</p>
                      <p className="text-indigo-900 text-sm mt-1">{field.paymentNote}</p>
                    </div>
                  )}
                  <p className="text-xs text-indigo-800">
                    Ödemeyi havale/EFT ile yaptıktan sonra “Ödemeyi Onayla” ile rezervasyonu tamamlayabilirsiniz.
                  </p>
                </div>
              ) : (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <p className="text-sm text-amber-800">
                    Bu saha için ödeme bilgisi henüz girilmemiş. Lütfen saha sahibi ile iletişime geçin.
                  </p>
                </div>
              )}
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => setStep('select')}
                className="flex-1 bg-white border border-gray-300 text-gray-700 font-semibold py-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Geri
              </button>
              <button
                onClick={handlePaymentConfirm}
                disabled={!teamName || !contactName || !contactPhone}
                className="flex-1 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-200 disabled:text-gray-400 text-white font-semibold py-3 rounded-lg transition-colors"
              >
                Talebi Gönder
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Success */}
        {step === 'success' && (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Rezervasyon Talebiniz Alındı</h2>
            <p className="text-gray-500 mb-8 max-w-md mx-auto">
              Saha sahibi önce talebi onaylayacak. Onaydan sonra profilinizden ödemeyi tamamlayıp rezervasyonu kesinleştirebilirsiniz.
            </p>
            {dispatchInfo && (
              <p className="text-sm text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-lg px-4 py-2 max-w-2xl mx-auto mb-6">
                {dispatchInfo}
              </p>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 max-w-md mx-auto mb-8">
              <div className="space-y-3 text-left">
                <div className="flex justify-between">
                  <span className="text-gray-500 text-sm">Rezervasyon No</span>
                  <span className="text-gray-900 font-mono font-semibold">{reservationCode || 'RSV-OLUSTURULDU'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 text-sm">Saha</span>
                  <span className="text-gray-900 font-medium">{field.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 text-sm">Tarih</span>
                  <span className="text-gray-900 font-medium">{formatDate(selectedDate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 text-sm">Saat</span>
                  <span className="text-gray-900 font-medium">
                    {formatHourValue(selectedHour || 0)} - {formatHourValue((selectedHour || 0) + slotDurationHours)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 text-sm">Takım</span>
                  <span className="text-gray-900 font-medium">{teamName}</span>
                </div>
                <div className="border-t border-gray-200 pt-3 flex justify-between">
                  <span className="text-gray-500 text-sm">Ödenecek Tutar</span>
                  <span className="text-primary-600 font-bold">{totalPrice}₺</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4">
              <button
                onClick={() => navigate('/')}
                className="bg-white border border-gray-300 text-gray-700 font-semibold px-8 py-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Ana Sayfaya Dön
              </button>
              <button
                onClick={() => navigate('/sahalar')}
                className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
              >
                Daha Fazla Saha Keşfet
              </button>
            </div>
          </div>
        )}
        {error && (
          <div className="mb-6 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingPage;
